import React, { SyntheticEvent, useEffect, useState } from 'react';

import { ButtonQuaternary, SpacerVertical, SpacerHorizontal } from '@klarna/bubble-ui';
import { HorizontalWrapper } from 'moet-core';

import { CallToActionButton, SecondaryButton, ToggleWrappedContent } from '@/components';
import { OnboardingField } from '@/bpc/components/OnboardingFieldComponent/OnboardingField';
import { OnboardingFieldSetComponent } from '@/bpc/components/OnboardingFieldComponent/OnboardingFieldSetComponent';
import { PersonRole } from '@mjolnir/mf-fields-ts/src/enm/PersonRole';
import { OnboardingGroup } from '@/bpc/components/OnboardingGroupComponent/OnboardingGroup';

const tPrefix = 'products-fe.general.buttons';

interface StakeholderSubGroupComponentProps {
  canGoBack: boolean;
  canSkipRole: boolean;
  forceUpdate: () => void;
  group: OnboardingGroup;
  isLoading: boolean;
  max: number | undefined;
  min: number | undefined;
  onBack: (e: SyntheticEvent) => void;
  onNext: (e: SyntheticEvent) => void;
  role: PersonRole;
  stakeholderItems: OnboardingField[][];
  t: TranslatorFunction;
  tKey: TranslatorFunctionKey;
}

export const StakeholderSubGroupComponent: React.FC<StakeholderSubGroupComponentProps> = ({
  canGoBack = false,
  canSkipRole = false,
  forceUpdate = (): undefined => undefined,
  group,
  isLoading = false,
  max = Number.MAX_SAFE_INTEGER,
  min = 0,
  onBack = (): undefined => undefined,
  onNext = (): undefined => undefined,
  role,
  stakeholderItems = [],
  t,
  tKey,
}: StakeholderSubGroupComponentProps) => {
  const [yesNo, setYesNo] = useState(stakeholderItems.length || !canSkipRole ? true : undefined);

  useEffect(() => {
    if (yesNo && stakeholderItems.length < min) {
      addItems(min - stakeholderItems.length);
    }
  }, []);

  const addItems = (number = 1): void => {
    Array(number)
      .fill(null)
      .forEach(() => {
        const addedFieldSet = group.addItem();
        const field = addedFieldSet.find(f =>
          f.absolutePath.endsWith('person_role'),
        ) as OnboardingField;
        group.setValueOfField(field, [role]);
      });

    forceUpdate();
  };

  const onAddItem = (event: any | undefined): void => {
    // eslint-disable-next-line no-unused-expressions
    event?.preventDefault();
    addItems(1);
  };

  const onDeleteItem = (index: number): void => {
    group.deleteItem(index);
    forceUpdate();
  };

  const removeStakeholdersWithRole = (role: PersonRole): void => {
    group.fields.forEach((fields, index) => {
      const roleField = fields.find(f => f.absolutePath.endsWith('person_role')) as OnboardingField;
      const value = group.getValueForField(roleField.absolutePath) as Array<PersonRole>;

      if (!value) {
        return;
      }

      if (value.includes(role) && value.length === 1) {
        onDeleteItem(index);
      } else {
        group.setValueOfField(
          roleField,
          value.filter(v => v !== role),
        );
      }
    });
    forceUpdate();
  };

  const onBlurOfField = (field: OnboardingField) => (value): void => {
    group.setValueOfField(field, value);
    // to recalculate whether to disable Next button // TODO is this really needed?
    forceUpdate();
  };

  const onToggle = (toggleValue: boolean): void => {
    if (yesNo && !toggleValue) {
      removeStakeholdersWithRole(role);
    } else if (!yesNo && toggleValue) {
      addItems(min);
    }
    setYesNo(toggleValue);
  };

  const Content = (): React.JSX.Element => (
    <>
      {stakeholderItems.map((fields, i) => (
        // TODO don't use index as key prop
        // https://robinpokorny.com/blog/index-as-a-key-is-an-anti-pattern/
        <React.Fragment key={`item-${i}`}>
          <OnboardingFieldSetComponent
            fields={fields.filter(f => !f.absolutePath.endsWith('person_role'))}
            getValueForField={group.getValueForField}
            setValueOfField={group.setValueOfField}
            onBlurField={onBlurOfField}
            onDelete={
              stakeholderItems.length > min ? (): void | undefined => onDeleteItem(i) : undefined
            }
            t={t}
            tKey={tKey}
            hasCardLayout={!(min === 1 && max === 1)}
            highlightMandatoryButEmptyFields={false}
          />
          <SpacerVertical spaceToken="space/150" />
        </React.Fragment>
      ))}
      {stakeholderItems.length < max && (
        <>
          <div
            style={{
              flex: 0,
              display: 'flex',
              justifyContent: 'flex-start',
            }}
          >
            <ButtonQuaternary size="small" onClick={onAddItem}>
              {t(`${tPrefix}.add_aditional_item`)}
            </ButtonQuaternary>
          </div>
          <SpacerVertical spaceToken="space/150" />
        </>
      )}
    </>
  );

  const allMandatoryFilled = group.allMandatoryFilled(canSkipRole ? 0 : min);

  return (
    <>
      {canSkipRole ? (
        <ToggleWrappedContent
          error={false}
          isDisabled={false}
          toggleOnChange={onToggle}
          toggleValue={yesNo}
          yesContent={<Content />}
        />
      ) : (
        <Content />
      )}
      <HorizontalWrapper display="inline-flex" alignItems="center" justifyContent="flex-end">
        {canGoBack && (
          <>
            <SecondaryButton onClick={onBack}>{t(`${tPrefix}.back`)}</SecondaryButton>
            <SpacerHorizontal spaceToken="space/300" />
          </>
        )}

        <CallToActionButton
          disabled={!allMandatoryFilled || yesNo === undefined}
          onClick={onNext}
          loading={isLoading}
        >
          {t(`${tPrefix}.continue`)}
        </CallToActionButton>
      </HorizontalWrapper>
    </>
  );
};
